import { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';
import { getSavedIdeas, deleteSavedIdea, SavedContentIdea, Platform } from '../../lib/api';

export default function IdeasGalleryPage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { workspaceSlug } = useParams<{ workspaceSlug: string }>();

  const [ideas, setIdeas] = useState<SavedContentIdea[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [deletingId, setDeletingId] = useState<string | null>(null);

  // Filters
  const [includeArchived, setIncludeArchived] = useState(false);
  const [platformFilter, setPlatformFilter] = useState<Platform | 'all'>('all');
  const [engagementFilter, setEngagementFilter] = useState<'all' | 'low' | 'medium' | 'high'>('all');

  // Load saved ideas
  useEffect(() => {
    loadIdeas();
  }, [includeArchived]);

  const loadIdeas = async () => {
    setLoading(true);
    setError(null);

    try {
      const data = await getSavedIdeas({
        includeArchived,
        limit: 100,
      });
      setIdeas(data);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load saved ideas');
    } finally {
      setLoading(false);
    }
  };

  const handleUseIdea = (idea: SavedContentIdea) => {
    navigate(`/${workspaceSlug}/create?type=idea`, {
      state: {
        idea: idea.description,
        platforms: idea.suggestedPlatforms,
        savedIdeaId: idea.id,
      },
    });
  };

  const handleDeleteIdea = async (ideaId: string) => {
    if (!confirm(t('create.gallery.deleteConfirm'))) {
      return;
    }

    setDeletingId(ideaId);

    try {
      await deleteSavedIdea(ideaId);
      setIdeas((prev) => prev.filter((idea) => idea.id !== ideaId));
      toast.success(t('create.gallery.deleteSuccess'));
    } catch (err) {
      toast.error(t('create.gallery.deleteError'));
    } finally {
      setDeletingId(null);
    }
  };

  const getEngagementColor = (engagement: string) => {
    switch (engagement) {
      case 'high':
        return 'text-green-600 dark:text-green-400';
      case 'medium':
        return 'text-yellow-600 dark:text-yellow-400';
      case 'low':
        return 'text-gray-600 dark:text-gray-400';
      default:
        return 'text-gray-600';
    }
  };

  const getPlatformIcon = (platform: string) => {
    const icons: Record<string, string> = {
      linkedin: '💼',
      instagram: '📷',
      facebook: '📘',
      twitter: '🐦',
      youtube: '▶️',
      tiktok: '🎵',
      pinterest: '📌',
    };
    return icons[platform] || '📱';
  };

  // Apply filters
  const filteredIdeas = ideas.filter((idea) => {
    if (platformFilter !== 'all' && !idea.suggestedPlatforms.includes(platformFilter)) {
      return false;
    }
    if (engagementFilter !== 'all' && idea.estimatedEngagement !== engagementFilter) {
      return false;
    }
    return true;
  });

  return (
    <div className="max-w-6xl mx-auto p-8">
      {/* Header */}
      <div className="mb-8">
        <button
          onClick={() => navigate(`/${workspaceSlug}/dashboard`)}
          className="inline-flex items-center text-gray-600 dark:text-gray-400 hover:text-black dark:hover:text-white mb-4 transition-colors"
        >
          <svg className="w-5 h-5 mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 19l-7-7m0 0l7-7m-7 7h18" />
          </svg>
          {t('create.backToDashboard')}
        </button>

        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-4xl font-bold mb-3 dark:text-gray-100">{t('create.gallery.title')}</h1>
            <p className="text-lg text-gray-600 dark:text-gray-400">{t('create.gallery.subtitle')}</p>
          </div>
          <button
            onClick={() => navigate(`/${workspaceSlug}/create/ideas`)}
            className="px-6 py-3 bg-black dark:bg-white text-white dark:text-black border-2 border-black dark:border-white font-bold text-lg rounded-lg hover:bg-gray-800 dark:hover:bg-gray-200 transition-colors"
          >
            💡 {t('create.gallery.generateIdeasButton')}
          </button>
        </div>
      </div>

      {/* Filters */}
      <div className="bg-white dark:bg-gray-800 border-2 border-black dark:border-gray-600 rounded-lg p-6 mb-8">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          {/* Platform Filter */}
          <div>
            <label className="block mb-2 font-bold text-lg dark:text-gray-100">
              {t('create.gallery.platformFilter')}
            </label>
            <select
              value={platformFilter}
              onChange={(e) => setPlatformFilter(e.target.value as Platform | 'all')}
              className="w-full px-4 py-3 border-2 border-black dark:border-gray-600 rounded-lg text-lg focus:outline-none focus:ring-4 focus:ring-black/20 dark:focus:ring-white/20 dark:bg-gray-700 dark:text-gray-100"
            >
              <option value="all">{t('create.gallery.allPlatforms')}</option>
              <option value="linkedin">LinkedIn</option>
              <option value="instagram">Instagram</option>
              <option value="facebook">Facebook</option>
              <option value="twitter">Twitter</option>
              <option value="youtube">YouTube</option>
              <option value="tiktok">TikTok</option>
            </select>
          </div>

          {/* Engagement Filter */}
          <div>
            <label className="block mb-2 font-bold text-lg dark:text-gray-100">
              {t('create.gallery.engagementFilter')}
            </label>
            <select
              value={engagementFilter}
              onChange={(e) => setEngagementFilter(e.target.value as 'all' | 'low' | 'medium' | 'high')}
              className="w-full px-4 py-3 border-2 border-black dark:border-gray-600 rounded-lg text-lg focus:outline-none focus:ring-4 focus:ring-black/20 dark:focus:ring-white/20 dark:bg-gray-700 dark:text-gray-100"
            >
              <option value="all">{t('create.gallery.allEngagement')}</option>
              <option value="high">{t('create.ideas.engagement.high')}</option>
              <option value="medium">{t('create.ideas.engagement.medium')}</option>
              <option value="low">{t('create.ideas.engagement.low')}</option>
            </select>
          </div>

          {/* Archive Toggle */}
          <div>
            <label className="block mb-2 font-bold text-lg dark:text-gray-100">
              {t('create.gallery.showArchived')}
            </label>
            <button
              onClick={() => setIncludeArchived(!includeArchived)}
              className={`w-full px-4 py-3 border-2 rounded-lg text-lg font-bold transition-colors ${
                includeArchived
                  ? 'border-black dark:border-white bg-black dark:bg-white text-white dark:text-black'
                  : 'border-black dark:border-gray-600 bg-white dark:bg-gray-800 text-black dark:text-gray-100 hover:bg-gray-100 dark:hover:bg-gray-700'
              }`}
            >
              {includeArchived ? t('create.gallery.showingArchived') : t('create.gallery.hideArchived')}
            </button>
          </div>
        </div>
      </div>

      {/* Loading State */}
      {loading && (
        <div className="text-center py-16">
          <div className="animate-spin h-12 w-12 border-4 border-black dark:border-white border-t-transparent dark:border-t-transparent rounded-full mx-auto mb-4"></div>
          <p className="text-gray-600 dark:text-gray-400">{t('create.gallery.loading')}</p>
        </div>
      )}

      {/* Error State */}
      {error && (
        <div className="bg-red-50 dark:bg-red-900/20 border-2 border-red-500 rounded-lg p-4 mb-8">
          <p className="text-red-800 dark:text-red-300 font-bold">{error}</p>
        </div>
      )}

      {/* Ideas Grid */}
      {!loading && !error && filteredIdeas.length > 0 && (
        <div>
          <div className="flex justify-between items-center mb-6">
            <h2 className="text-2xl font-bold dark:text-gray-100">
              {t('create.gallery.resultsCount', { count: filteredIdeas.length })}
            </h2>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {filteredIdeas.map((idea) => (
              <div
                key={idea.id}
                className={`bg-white dark:bg-gray-800 border-2 rounded-lg p-6 ${
                  idea.isArchived
                    ? 'border-gray-400 dark:border-gray-600 opacity-60'
                    : 'border-black dark:border-gray-600'
                }`}
              >
                {/* Header with badges */}
                <div className="flex items-center gap-2 mb-4 flex-wrap">
                  <span
                    className={`text-xs font-bold uppercase px-2 py-1 border-2 border-black dark:border-gray-600 rounded ${getEngagementColor(idea.estimatedEngagement)}`}
                  >
                    {t(`create.ideas.engagement.${idea.estimatedEngagement}`)}
                  </span>
                  {(idea.usedAt || idea.useCount > 0) && (
                    <span className="text-xs font-bold text-green-600 dark:text-green-400 px-2 py-1 border-2 border-green-600 dark:border-green-400 rounded">
                      ✓ {idea.useCount > 0 ? t('create.gallery.usedCount', { count: idea.useCount }) : t('create.gallery.used')}
                    </span>
                  )}
                  {idea.isArchived && (
                    <span className="text-xs font-bold text-gray-600 dark:text-gray-400 px-2 py-1 border-2 border-gray-600 dark:border-gray-400 rounded">
                      {t('create.gallery.archived')}
                    </span>
                  )}
                </div>

                {/* Title */}
                <h3 className="text-xl font-bold mb-3 dark:text-gray-100">{idea.title}</h3>

                {/* Description */}
                <p className="text-gray-700 dark:text-gray-300 mb-4 line-clamp-3">{idea.description}</p>

                {/* Trend Context */}
                {idea.trendTopic && (
                  <div className="bg-yellow-50 dark:bg-yellow-900/20 border-l-4 border-yellow-500 p-3 mb-4">
                    <p className="text-sm text-yellow-900 dark:text-yellow-300">
                      <span className="font-bold">{t('create.ideas.trendingTopic')}:</span> {idea.trendTopic}
                    </p>
                  </div>
                )}

                {/* Platforms */}
                <div className="flex flex-wrap gap-2 mb-4">
                  {idea.suggestedPlatforms.map((platform) => (
                    <span
                      key={platform}
                      className="inline-flex items-center px-3 py-1 border-2 border-black dark:border-gray-600 rounded text-sm font-bold dark:text-gray-100"
                    >
                      <span className="mr-1">{getPlatformIcon(platform)}</span>
                      {t(`platforms.${platform}`)}
                    </span>
                  ))}
                </div>

                {/* Metadata */}
                <div className="text-xs text-gray-500 dark:text-gray-400 mb-4">
                  <div>
                    {t('create.gallery.savedOn', {
                      date: new Date(idea.savedAt).toLocaleDateString(),
                    })}
                  </div>
                  {idea.usedPostId && (
                    <div className="mt-1">
                      <a
                        href={`/${workspaceSlug}/approve?post=${idea.usedPostId}`}
                        className="text-blue-600 dark:text-blue-400 hover:underline"
                        onClick={(e) => {
                          e.preventDefault();
                          navigate(`/${workspaceSlug}/approve?post=${idea.usedPostId}`);
                        }}
                      >
                        {t('create.gallery.viewPost')} →
                      </a>
                    </div>
                  )}
                </div>

                {/* Action Buttons */}
                <div className="flex gap-3">
                  <button
                    onClick={() => handleUseIdea(idea)}
                    disabled={idea.isArchived}
                    className={`flex-1 px-4 py-2 border-2 font-bold rounded transition-colors ${
                      idea.isArchived
                        ? 'border-gray-400 dark:border-gray-600 bg-gray-100 dark:bg-gray-700 text-gray-400 dark:text-gray-500 cursor-not-allowed'
                        : 'border-black dark:border-white bg-black dark:bg-white text-white dark:text-black hover:bg-gray-800 dark:hover:bg-gray-200'
                    }`}
                  >
                    {t('create.gallery.useButton')}
                  </button>
                  <button
                    onClick={() => handleDeleteIdea(idea.id)}
                    disabled={deletingId === idea.id}
                    className="px-4 py-2 border-2 border-red-600 dark:border-red-400 text-red-600 dark:text-red-400 font-bold rounded hover:bg-red-50 dark:hover:bg-red-900/20 transition-colors disabled:opacity-50"
                  >
                    {deletingId === idea.id ? '...' : t('create.gallery.deleteButton')}
                  </button>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Empty State */}
      {!loading && !error && filteredIdeas.length === 0 && (
        <div className="text-center py-16">
          <div className="text-6xl mb-4">💡</div>
          <h3 className="text-xl font-bold mb-2 dark:text-gray-100">{t('create.gallery.emptyTitle')}</h3>
          <p className="text-gray-600 dark:text-gray-400 mb-6">{t('create.gallery.emptyDescription')}</p>
          <button
            onClick={() => navigate(`/${workspaceSlug}/create/ideas`)}
            className="px-6 py-3 bg-black dark:bg-white text-white dark:text-black border-2 border-black dark:border-white font-bold rounded hover:bg-gray-800 dark:hover:bg-gray-200 transition-colors"
          >
            {t('create.gallery.generateIdeasButton')}
          </button>
        </div>
      )}
    </div>
  );
}
